"use client";

import { useRef } from "react";
import type { ReactNode } from "react";
import { CODE, GlowCard, GradText, SectionHead, SectionShell } from "./kit";
import { useEnterStagger } from "./kit/motion";

/** Blue wash rgba(47,107,224,0.065) composited over PAGE_FILL #f8fafc. */
const WASH_FILL = "#ebf1fa";

const TILES: { span: string; title: string; desc: string; snippet: ReactNode }[] = [
  {
    span: "md:col-span-3",
    title: "App configs",
    desc: "Ship remote configuration from a stable URL — no backend deploy, no app release.",
    snippet: (
      <>
        <span className={CODE.key}>&quot;theme&quot;</span>:{" "}
        <span className={CODE.string}>&quot;dark&quot;</span>,{" "}
        <span className={CODE.key}>&quot;min_version&quot;</span>:{" "}
        <span className={CODE.string}>&quot;2.4.0&quot;</span>
      </>
    ),
  },
  {
    span: "md:col-span-2",
    title: "Feature flags",
    desc: "Flip features on and off without redeploying anything.",
    snippet: (
      <>
        <span className={CODE.key}>&quot;new_checkout&quot;</span>:{" "}
        <span className={CODE.bool}>true</span>
      </>
    ),
  },
  {
    span: "md:col-span-2",
    title: "Deep links",
    desc: "One URL that resolves to the right screen on every platform.",
    snippet: (
      <>
        <span className={CODE.key}>&quot;ios&quot;</span>:{" "}
        <span className={CODE.string}>&quot;myshop://launch&quot;</span>
      </>
    ),
  },
  {
    span: "md:col-span-3",
    title: "Lightweight APIs",
    desc: "A link that answers like an endpoint — JSON in one request, no server to run.",
    snippet: (
      <>
        <span className={CODE.comment}>$</span> curl{" "}
        <span className={CODE.string}>&quot;rii.link/launch?format=json&quot;</span>
      </>
    ),
  },
];

/**
 * Use-case bento on the kit's translucent blue wash band (first wash use).
 * The wash is see-through, so clearing blocks fill with the composited
 * wash-over-page color instead of the page color.
 */
export default function UseCaseBentoSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  useEnterStagger(rootRef, "[data-glow-card]", { y: 26, stagger: 0.09 });

  return (
    <SectionShell id="ref-response-uses" band="blue">
      <SectionHead
        center
        eyebrow="What people build"
        gridFill={WASH_FILL}
        title={
          <>
            A link that <GradText>does the backend&apos;s job.</GradText>
          </>
        }
        lead="On Premium, a URL can answer with structured data you write — and a surprising amount of infrastructure becomes optional."
      />

      <div data-grid-block data-grid-fill={WASH_FILL}>
        <div ref={rootRef} className="grid gap-4 md:grid-cols-5">
          {TILES.map((t) => (
            <GlowCard key={t.title} span={t.span}>
              <div className="rounded-[9px] bg-[#0d0d12] px-3.5 py-2.5 font-jetbrains text-[12.5px] text-white/85 [overflow-wrap:anywhere]">
                {t.snippet}
              </div>
              <h3 className="mt-4 text-[17px] font-bold tracking-[-0.01em]">{t.title}</h3>
              <p className="mt-1.5 text-[14px] leading-[1.6] text-muted">{t.desc}</p>
            </GlowCard>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}
